import { asLanguage } from '@rbilabs/intl';

import { SupportedLanguages } from '@rbi-ctg/frontend';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import {
  extractLanguageCode,
  getDefaultMarketLanguage,
  getSupportedMarketLanguage,
  getSupportedMarketLanguages,
  isSupportedMarketLanguage,
} from './language';

export const LANGUAGE_SEARCH_PARAM = 'lang';

export const getLanguageFromUrlPath = (pathname: string) => {
  const maybeLanguageCode = pathname.split('/')?.[1] || '';

  return asLanguage(maybeLanguageCode);
};

export const getLanguageFromQueryString = (queryString: string) =>
  new URLSearchParams(queryString).get(LANGUAGE_SEARCH_PARAM);

export const getLanguageFromUserPreferences = () =>
  LocalStorage.getItem<SupportedLanguages>(StorageKeys.LANGUAGE);

export const getNavigatorLanguage = () => {
  const maybeLocale = window?.navigator?.languages?.[0] || window?.navigator?.language || '';

  return extractLanguageCode(maybeLocale);
};

/**
 * Retrieves the language requested by the user.
 *
 * If the requested language is unknown, the market's default language will be returned.
 *
 * @param shouldUseLanguageCodeInUrls whether language code in URLs are in use or not.
 * @returns Language
 */
export const getInferredLanguage = (shouldUseLanguageCodeInUrls = false) => {
  const languageRequested =
    (shouldUseLanguageCodeInUrls && getLanguageFromUrlPath(window.location.pathname)) ||
    getLanguageFromQueryString(window.location.search) ||
    getLanguageFromUserPreferences() ||
    getNavigatorLanguage();

  return asLanguage(languageRequested) || getDefaultMarketLanguage();
};

/**
 * Retrieves the application language.
 *
 * The user-requested language will be taken into account only when the market
 * is multilingual or when the `useDefault` argument is set to true.
 *
 * @param useDefault when true, the default market language is used, hence
 *  ignoring any explicit language requests.
 * @param shouldUseLanguageCodeInUrls when true, support for language code in
 *  URLs will be enabled.
 * @returns Language
 */
export const getAppLanguage = (useDefault = false, shouldUseLanguageCodeInUrls = false) => {
  return getSupportedMarketLanguage(getInferredLanguage(shouldUseLanguageCodeInUrls), useDefault);
};

/**
 * Given a set of LD flags, determines whether the market should use language
 * codes in its URL paths or not.
 *
 * @param enableLanguageCodeInUrls value of `rollout-enable-language-code-in-urls` LD flag.
 * @param disableLocalization value of `disableLocalization` LD flag.
 * @returns whether language codes should be used in URL paths or not.
 */
export const shouldUseLanguageCodeInUrls = (
  enableLanguageCodeInUrls: boolean,
  disableLocalization: boolean
) => {
  const marketIsMultilingual = getSupportedMarketLanguages().length > 1;
  return enableLanguageCodeInUrls && !disableLocalization && marketIsMultilingual;
};

/**
 * Determines whether a path is eligible or not for a redirect that includes the
 * language code prepended to the path.
 *
 * The assumption for calling this function is that
 * `shouldUseLanguageCodeInUrls()` returns true.
 *
 * @param pathname
 * @returns whether a redirect should happen or not.
 */
export const shouldRedirectToLanguagePath = (pathname: string) => {
  const languageOrPath = getLanguageFromUrlPath(pathname);
  return !languageOrPath || (languageOrPath && !isSupportedMarketLanguage(languageOrPath));
};
